import React, { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  Button,
  EditIcon,
  Skeleton,
  Tag,
  Text,
  Tooltip,
  TrashIcon,
} from '@mercai/clever'

import { DECIMAL_PLACES_DEFAULT } from '@/constants'
import { formatCurrency } from '@/helpers'
import { ROUTES_ADMIN } from '@/router'

import { CardSupplyOrderProps } from './card-supply-order.type'
import { getDestinationAddress } from './card-supply-order-constants'
import styles from './card-supply-order.module.css'

export const CardSupplyOrder = ({
  supplyOrder,
  isLoading,
  saleOrderDestinationAddress,
  onRemove,
}: CardSupplyOrderProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenOrderCenterPage')

  const statuses: Record<string, ReactNode> = {
    pending: (
      <Tag variant="warning" model="light">
        {tEnums('supply_order_status_pending')}
      </Tag>
    ),
    completed: (
      <Tag variant="success" model="light">
        {tEnums('supply_order_status_completed')}
      </Tag>
    ),
  }

  const Loading = () => {
    return (
      <div>
        <div className={styles['supply-order-card']}>
          <div className={styles['supply-order-card-header']}>
            <div>
              <div className={styles['supplier-name-container']}>
                <Skeleton height="1.2rem" width="10rem" />
              </div>

              <Text variant="text-small" className={styles.address}>
                <Skeleton height="1rem" width="10rem" />
              </Text>
            </div>

            <div className={styles['actions-container']}>
              <div>
                <div>
                  <Text variant="title-base">
                    <Skeleton height="1.2rem" width="5rem" />
                  </Text>
                </div>

                <Text
                  className={styles['supply-order-reference']}
                  variant="text-small"
                >
                  <Skeleton height="1rem" width="5rem" />
                </Text>
              </div>

              <Skeleton height="2rem" width="2rem" />
              <Skeleton height="2rem" width="2rem" />
            </div>
          </div>
          <div className={styles['supply-order-card-body']}>
            <div className={styles['supply-order-card-body-item']}>
              <Skeleton height="1rem" width="10rem" />
            </div>
            <div className={styles['supply-order-card-body-item']}>
              <Skeleton height="1rem" width="5rem" />
            </div>
            <div className={styles['supply-order-card-body-item']}>
              <Skeleton height="1rem" width="6rem" />
            </div>
            <div className={styles['supply-order-card-body-item']}>
              <Skeleton height="1rem" width="6rem" />
            </div>
            <div className={styles['supply-order-card-body-item-total']}>
              <Skeleton height="1rem" width="7rem" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return <Loading />
  }

  return (
    <div className={styles['supply-order-card']}>
      <div className={styles['supply-order-card-header']}>
        <div>
          <div className={styles['supplier-name-container']}>
            <Text variant="title-medium">
              {supplyOrder?.supplier?.name || '-'}
            </Text>
            {statuses[supplyOrder?.status || 'pending']}
          </div>

          <Text
            variant="text-small"
            className={styles.address}
          >{`${saleOrderDestinationAddress || getDestinationAddress(supplyOrder)}`}</Text>
        </div>

        <div className={styles['actions-container']}>
          <div>
            <div>
              <Text variant="title-base">
                {supplyOrder?.supplyQuotation?.purchaseOrderCode}
              </Text>
            </div>
            <Link
              target="_blank"
              to={ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.PURCHASE_ORDER.SHOW.fullPath(supplyOrder?.uuid || '')}
            >
              <Text
                className={styles['supply-order-reference']}
                variant="text-small"
              >
                {supplyOrder?.reference}
              </Text>
            </Link>
          </div>

          <Button
            variant={'secondary'}
            model="round"
            size="small"
            target="_blank"
            href={ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.PURCHASE_ORDER.UPDATE.fullPath(
              supplyOrder?.uuid || '',
            )}
          >
            <EditIcon />
          </Button>
          <Button
            variant={'secondary'}
            model="round"
            size="small"
            onClick={() =>
              onRemove && supplyOrder?.uuid && onRemove(supplyOrder.uuid)
            }
          >
            <TrashIcon />
          </Button>
        </div>
      </div>
      <div className={styles['supply-order-card-body']}>
        <div className={styles['supply-order-card-body-item-header']}>
          {t('orderCenterShowTabSupplierColumnProductColumn')}
        </div>
        <div className={styles['supply-order-card-body-item-header']}>
          {t('orderCenterShowTabSupplierColumnCostColumn')}
          <Text>
            {t('orderCenterShowTabSupplierColumnCostWithoutIvaColumn')}
          </Text>
        </div>
        <div className={styles['supply-order-card-body-item-header']}>
          {t('orderCenterShowTabSupplierColumnQuantityColumn')}
        </div>
        <div className={styles['supply-order-card-body-item-header']}>
          {t('orderCenterShowTabSupplierColumnCategoryColumn')}
        </div>
        <div className={styles['supply-order-card-body-item-header-total']}>
          {t('orderCenterShowTabSupplierColumnTotalColumn')}
          <Text>
            {t('orderCenterShowTabSupplierColumnTotalWithoutIvaColumn')}
          </Text>
        </div>

        {supplyOrder?.items?.map((item, index) => (
          <React.Fragment key={index}>
            <div className={styles['supply-order-card-body-item']}>
              <Tooltip
                text={item?.catalogProduct?.attributes?.title || ''}
                className={styles.tooltip}
                orientation="top"
              >
                <Text className={styles['product-title']}>
                  {item?.catalogProduct?.attributes?.title || ''}
                </Text>
              </Tooltip>
            </div>

            <div className={styles['supply-order-card-body-item']}>
              <p className={styles['values-box']}>
                <Text variant="text-small">
                  {formatCurrency(
                    Number(item?.cost || 0),
                    'es-MX',
                    supplyOrder?.payments?.[0]?.currency || 'MXN',
                    DECIMAL_PLACES_DEFAULT,
                  )}
                </Text>
                {` `}
                <Text variant="text-xsmall" className={styles['without-iva']}>
                  {`(${formatCurrency(
                    Number(item?.costWithoutIva || 0),
                    'es-MX',
                    supplyOrder?.payments?.[0]?.currency || 'MXN',
                    DECIMAL_PLACES_DEFAULT,
                  )})`}
                </Text>
              </p>
            </div>

            <div className={styles['supply-order-card-body-item']}>
              <Text variant="text-small">{item?.quantity || ''} kg</Text>
            </div>

            <div className={styles['supply-order-card-body-item']}>
              <Text variant="text-small">
                {item?.category
                  ? tEnums(`product_quality_category_${item?.category}`)
                  : '-'}
              </Text>
            </div>

            <div className={styles['supply-order-card-body-item-total']}>
              <p className={styles['values-box-total']}>
                <Text variant="title-base">
                  {formatCurrency(
                    Number(item?.cost || 0) * (item?.quantity || 1),
                    'es-MX',
                    supplyOrder?.payments?.[0]?.currency || 'MXN',
                  )}
                </Text>{' '}
                <Text variant="text-small" className={styles['without-iva']}>
                  (
                  {formatCurrency(
                    item?.totalWithoutIva
                      ? Number(item?.totalWithoutIva || 0)
                      : 0,
                    'es-MX',
                    supplyOrder?.payments?.[0]?.currency || 'MXN',
                  )}
                  )
                </Text>
              </p>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}
